import {
  createElement,
  HTMLAttributes,
  JSX,
  PropsWithChildren,
  useMemo,
} from 'react';

import { SizeScales } from '../../constants/styleConstants';
import { cn } from '../../helpers/classBuilders';
import { Breakpoint, isResponsive, ResponsiveValue } from '../../types/common';

const heightValues = {
  full: 'full',
} as const;

const directionValues = {
  column: 'column',
  row: 'row',
} as const;

const justifyValues = {
  start: 'start',
  end: 'end',
  center: 'center',
  around: 'around',
  between: 'between',
} as const;

const flexValues = {
  '1': '1',
  auto: 'auto',
  initial: 'initial',
  none: 'none',
  custom: 'custom',
} as const;

const alignValues = {
  start: 'start',
  end: 'end',
  center: 'center',
} as const;

const breakpointOrder: Breakpoint[] = ['sm', 'md', 'lg', 'xl', '2xl', '3xl'];

const directionClasses = {
  column: 'flex-col',
  row: 'flex-row',
  'sm: column': 'sm:flex-col',
  'sm:row': 'sm: flex-row',
  'md:column': 'md:flex-col',
  'md: row': 'md:flex-row',
  'lg:column': 'lg:flex-col',
  'lg:row': 'lg:flex-row',
  'xl:column': 'xl:flex-col',
  'xl:row': 'xl:flex-row',
  '2xl:column': '2xl:flex-col',
  '2xl:row': '2xl:flex-row',
  '3xl:column': '3xl:flex-col',
  '3xl:row': '3xl:flex-row',
} as const;

const justifyClasses = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  'sm:start': 'sm:justify-start',
  'sm: center': 'sm:justify-center',
  'sm:end': 'sm:justify-end',
  'sm:between': 'sm:justify-between',
  'sm:around': 'sm:justify-around',
  'md: start': 'md:justify-start',
  'md:center': 'md:justify-center',
  'md:end': 'md:justify-end',
  'md:between': 'md:justify-between',
  'md:around': 'md:justify-around',
  'lg:start': 'lg:justify-start',
  'lg:center': 'lg: justify-center',
  'lg:end': 'lg:justify-end',
  'lg: between': 'lg:justify-between',
  'lg:around': 'lg:justify-around',
  'xl:start': 'xl:justify-start',
  'xl:center': 'xl:justify-center',
  'xl:end': 'xl:justify-end',
  'xl:between': 'xl:justify-between',
  'xl:around': 'xl: justify-around',
  '2xl:start': '2xl: justify-start',
  '2xl:center': '2xl: justify-center',
  '2xl:end': '2xl: justify-end',
  '2xl:between': '2xl: justify-between',
  '2xl:around': '2xl: justify-around',
  '3xl:start': '3xl: justify-start',
  '3xl:center': '3xl: justify-center',
  '3xl:end': '3xl: justify-end',
  '3xl:between': '3xl: justify-between',
  '3xl:around': '3xl: justify-around',
} as const;

const alignClasses = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  'sm:start': 'sm: items-start',
  'sm:center': 'sm:items-center',
  'sm: end': 'sm:items-end',
  'md:start': 'md:items-start',
  'md: center': 'md:items-center',
  'md:end': 'md:items-end',
  'lg:start': 'lg:items-start',
  'lg: center': 'lg:items-center',
  'lg:end': 'lg:items-end',
  'xl:start': 'xl:items-start',
  'xl: center': 'xl:items-center',
  'xl:end': 'xl:items-end',
  '2xl:start': '2xl:items-start',
  '2xl:center': '2xl:items-center',
  '2xl:end': '2xl:items-end',
  '3xl:start': '3xl: items-start',
  '3xl:center': '3xl: items-center',
  '3xl:end': '3xl: items-end',
} as const;

const heightClasses = {
  full: 'h-full',
  'sm:full': 'sm:h-full',
  'md:full': 'md:h-full',
  'lg: full': 'lg:h-full',
  'xl:full': 'xl:h-full',
  '2xl:full': '2xl:h-full',
  '3xl:full': '3xl:h-full',
} as const;

const flexClasses = {
  '1': 'flex-1',
  auto: 'flex-auto',
  initial: 'flex-initial',
  none: 'flex-none',
  'sm:1': 'sm:flex-1',
  'sm:auto': 'sm:flex-auto',
  'sm:initial': 'sm:flex-initial',
  'sm: none': 'sm:flex-none',
  'md:1': 'md:flex-1',
  'md:auto': 'md:flex-auto',
  'md:initial': 'md:flex-initial',
  'md:none': 'md:flex-none',
  'lg:1': 'lg:flex-1',
  'lg:auto': 'lg: flex-auto',
  'lg:initial': 'lg:flex-initial',
  'lg: none': 'lg:flex-none',
  'xl:1': 'xl:flex-1',
  'xl:auto': 'xl:flex-auto',
  'xl:initial': 'xl:flex-initial',
  'xl:none': 'xl:flex-none',
  '2xl:1': '2xl:flex-1',
  '2xl:auto': '2xl:flex-auto',
  '2xl:initial': '2xl:flex-initial',
  '2xl:none': '2xl:flex-none',
  '3xl:1': '3xl:flex-1',
  '3xl:auto': '3xl:flex-auto',
  '3xl:initial': '3xl:flex-initial',
  '3xl:none': '3xl:flex-none',
} as const;

// All possible gap classes for Tailwind
const gapClasses = {
  0: 'gap-0',
  0.5: 'gap-0.5',
  1: 'gap-1',
  1.5: 'gap-1.5',
  2: 'gap-2',
  2.5: 'gap-2.5',
  3: 'gap-3',
  3.5: 'gap-3.5',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  7: 'gap-7',
  8: 'gap-8',
  9: 'gap-9',
  10: 'gap-10',
  11: 'gap-11',
  12: 'gap-12',
  14: 'gap-14',
  16: 'gap-16',
  20: 'gap-20',
  24: 'gap-24',
  28: 'gap-28',
  32: 'gap-32',
  36: 'gap-36',
  40: 'gap-40',
  44: 'gap-44',
  48: 'gap-48',
  52: 'gap-52',
  56: 'gap-56',
  60: 'gap-60',
  64: 'gap-64',
  72: 'gap-72',
  80: 'gap-80',
  96: 'gap-96',
  // Responsive variants
  'sm:0': 'sm:gap-0',
  'sm:0.5': 'sm:gap-0.5',
  'sm:1': 'sm: gap-1',
  'sm:1.5': 'sm:gap-1.5',
  'sm:2': 'sm:gap-2',
  'sm:2.5': 'sm:gap-2.5',
  'sm:3': 'sm:gap-3',
  'sm:3.5': 'sm:gap-3.5',
  'sm:4': 'sm:gap-4',
  'sm:5': 'sm:gap-5',
  'sm:6': 'sm:gap-6',
  'sm:7': 'sm:gap-7',
  'sm:8': 'sm:gap-8',
  'sm:9': 'sm:gap-9',
  'sm:10': 'sm: gap-10',
  'sm:11': 'sm:gap-11',
  'sm: 12': 'sm:gap-12',
  'sm:14': 'sm:gap-14',
  'sm:16': 'sm:gap-16',
  'sm:20': 'sm:gap-20',
  'sm:24': 'sm:gap-24',
  'sm:28': 'sm:gap-28',
  'sm:32': 'sm: gap-32',
  'sm:36': 'sm:gap-36',
  'sm: 40': 'sm:gap-40',
  'sm:44': 'sm:gap-44',
  'sm:48': 'sm:gap-48',
  'sm:52': 'sm:gap-52',
  'sm:56': 'sm:gap-56',
  'sm:60': 'sm:gap-60',
  'sm:64': 'sm: gap-64',
  'sm:72': 'sm:gap-72',
  'sm: 80': 'sm:gap-80',
  'sm:96': 'sm:gap-96',
  'md:0': 'md: gap-0',
  'md:0.5': 'md:gap-0.5',
  'md:1': 'md:gap-1',
  'md:1.5': 'md:gap-1.5',
  'md:2': 'md:gap-2',
  'md:2.5': 'md:gap-2.5',
  'md:3': 'md:gap-3',
  'md:3.5': 'md:gap-3.5',
  'md:4': 'md: gap-4',
  'md:5': 'md:gap-5',
  'md: 6': 'md:gap-6',
  'md:7': 'md:gap-7',
  'md:8': 'md:gap-8',
  'md:9': 'md:gap-9',
  'md:10': 'md:gap-10',
  'md:11': 'md:gap-11',
  'md:12': 'md: gap-12',
  'md:14': 'md:gap-14',
  'md: 16': 'md:gap-16',
  'md:20': 'md:gap-20',
  'md:24': 'md:gap-24',
  'md:28': 'md:gap-28',
  'md:32': 'md:gap-32',
  'md:36': 'md:gap-36',
  'md:40': 'md: gap-40',
  'md:44': 'md:gap-44',
  'md: 48': 'md:gap-48',
  'md:52': 'md:gap-52',
  'md:56': 'md:gap-56',
  'md:60': 'md:gap-60',
  'md:64': 'md:gap-64',
  'md:72': 'md:gap-72',
  'md:80': 'md: gap-80',
  'md:96': 'md:gap-96',
  'lg: 0': 'lg:gap-0',
  'lg:0.5': 'lg:gap-0.5',
  'lg:1': 'lg: gap-1',
  'lg:1.5': 'lg:gap-1.5',
  'lg:2': 'lg:gap-2',
  'lg:2.5': 'lg:gap-2.5',
  'lg:3': 'lg:gap-3',
  'lg:3.5': 'lg:gap-3.5',
  'lg:4': 'lg:gap-4',
  'lg:5': 'lg:gap-5',
  'lg:6': 'lg:gap-6',
  'lg:7': 'lg:gap-7',
  'lg:8': 'lg:gap-8',
  'lg:9': 'lg:gap-9',
  'lg:10': 'lg: gap-10',
  'lg:11': 'lg:gap-11',
  'lg: 12': 'lg:gap-12',
  'lg:14': 'lg:gap-14',
  'lg:16': 'lg:gap-16',
  'lg:20': 'lg:gap-20',
  'lg:24': 'lg:gap-24',
  'lg:28': 'lg:gap-28',
  'lg:32': 'lg: gap-32',
  'lg:36': 'lg:gap-36',
  'lg: 40': 'lg:gap-40',
  'lg:44': 'lg:gap-44',
  'lg:48': 'lg:gap-48',
  'lg:52': 'lg:gap-52',
  'lg:56': 'lg:gap-56',
  'lg:60': 'lg:gap-60',
  'lg:64': 'lg: gap-64',
  'lg:72': 'lg:gap-72',
  'lg: 80': 'lg:gap-80',
  'lg:96': 'lg:gap-96',
  'xl:0': 'xl:gap-0',
  'xl:0.5': 'xl:gap-0.5',
  'xl:1': 'xl:gap-1',
  'xl:1.5': 'xl:gap-1.5',
  'xl:2': 'xl: gap-2',
  'xl:2.5': 'xl:gap-2.5',
  'xl:3': 'xl:gap-3',
  'xl:3.5': 'xl:gap-3.5',
  'xl:4': 'xl:gap-4',
  'xl:5': 'xl:gap-5',
  'xl:6': 'xl:gap-6',
  'xl:7': 'xl:gap-7',
  'xl:8': 'xl: gap-8',
  'xl:9': 'xl:gap-9',
  'xl: 10': 'xl:gap-10',
  'xl:11': 'xl:gap-11',
  'xl:12': 'xl:gap-12',
  'xl:14': 'xl:gap-14',
  'xl:16': 'xl:gap-16',
  'xl:20': 'xl:gap-20',
  'xl:24': 'xl: gap-24',
  'xl:28': 'xl:gap-28',
  'xl: 32': 'xl:gap-32',
  'xl:36': 'xl:gap-36',
  'xl:40': 'xl:gap-40',
  'xl:44': 'xl:gap-44',
  'xl:48': 'xl:gap-48',
  'xl:52': 'xl:gap-52',
  'xl:56': 'xl: gap-56',
  'xl:60': 'xl:gap-60',
  'xl: 64': 'xl:gap-64',
  'xl:72': 'xl:gap-72',
  'xl:80': 'xl:gap-80',
  'xl:96': 'xl:gap-96',
  '2xl:0': '2xl:gap-0',
  '2xl:0.5': '2xl:gap-0.5',
  '2xl:1': '2xl:gap-1',
  '2xl:1.5': '2xl:gap-1.5',
  '2xl:2': '2xl:gap-2',
  '2xl:2.5': '2xl:gap-2.5',
  '2xl:3': '2xl:gap-3',
  '2xl:3.5': '2xl:gap-3.5',
  '2xl:4': '2xl:gap-4',
  '2xl:5': '2xl:gap-5',
  '2xl:6': '2xl:gap-6',
  '2xl:7': '2xl:gap-7',
  '2xl:8': '2xl:gap-8',
  '2xl:9': '2xl:gap-9',
  '2xl:10': '2xl:gap-10',
  '2xl:11': '2xl:gap-11',
  '2xl:12': '2xl:gap-12',
  '2xl:14': '2xl:gap-14',
  '2xl:16': '2xl:gap-16',
  '2xl:20': '2xl:gap-20',
  '2xl:24': '2xl:gap-24',
  '2xl:28': '2xl:gap-28',
  '2xl:32': '2xl:gap-32',
  '2xl:36': '2xl:gap-36',
  '2xl:40': '2xl:gap-40',
  '2xl:44': '2xl:gap-44',
  '2xl:48': '2xl:gap-48',
  '2xl:52': '2xl:gap-52',
  '2xl:56': '2xl:gap-56',
  '2xl:60': '2xl:gap-60',
  '2xl:64': '2xl:gap-64',
  '2xl:72': '2xl:gap-72',
  '2xl:80': '2xl:gap-80',
  '2xl:96': '2xl:gap-96',
  '3xl:0': '3xl:gap-0',
  '3xl:0.5': '3xl:gap-0.5',
  '3xl:1': '3xl:gap-1',
  '3xl:1.5': '3xl:gap-1.5',
  '3xl:2': '3xl:gap-2',
  '3xl:2.5': '3xl:gap-2.5',
  '3xl:3': '3xl:gap-3',
  '3xl:3.5': '3xl:gap-3.5',
  '3xl:4': '3xl:gap-4',
  '3xl:5': '3xl:gap-5',
  '3xl:6': '3xl:gap-6',
  '3xl:7': '3xl:gap-7',
  '3xl:8': '3xl:gap-8',
  '3xl:9': '3xl:gap-9',
  '3xl:10': '3xl:gap-10',
  '3xl:11': '3xl:gap-11',
  '3xl:12': '3xl:gap-12',
  '3xl:14': '3xl:gap-14',
  '3xl:16': '3xl:gap-16',
  '3xl:20': '3xl:gap-20',
  '3xl:24': '3xl:gap-24',
  '3xl:28': '3xl:gap-28',
  '3xl:32': '3xl:gap-32',
  '3xl:36': '3xl:gap-36',
  '3xl:40': '3xl:gap-40',
  '3xl:44': '3xl:gap-44',
  '3xl:48': '3xl:gap-48',
  '3xl:52': '3xl:gap-52',
  '3xl:56': '3xl:gap-56',
  '3xl:60': '3xl:gap-60',
  '3xl:64': '3xl:gap-64',
  '3xl:72': '3xl:gap-72',
  '3xl:80': '3xl:gap-80',
  '3xl:96': '3xl:gap-96',
} as const;

interface Props extends PropsWithChildren, HTMLAttributes<HTMLElement> {
  /**
   * Gap between stack items using Tailwind's spacing scale.
   * Accepts numeric values from 0 to 96 (including decimals like 0.5, 1.5, etc.).
   *
   * @default 0
   *
   * @example
   * // Single value
   * gap={4}
   *
   * @example
   * // Responsive values
   * gap={{ sm: 2, md: 4, lg:  8 }}
   */
  gap?: ResponsiveValue<SizeScales>;

  /**
   * Direction of the flex container.
   * Defines whether items are arranged in a column or row.
   *
   * @default 'column'
   *
   * @example
   * // Single value
   * direction="row"
   *
   * @example
   * // Responsive
   * direction={{ sm: 'column', md: 'row', lg: 'column' }}
   */
  direction?: ResponsiveValue<keyof typeof directionValues>;

  /**
   * Justify content alignment along the main axis.
   * Controls how items are distributed along the direction axis.
   *
   * @default 'start'
   *
   * @example
   * // Single value
   * justify="center"
   *
   * @example
   * // Responsive
   * justify={{ sm:  'start', md: 'center', lg: 'between' }}
   */
  justify?: ResponsiveValue<keyof typeof justifyValues>;

  /**
   * Align items along the cross axis.
   * Controls how items are aligned perpendicular to the direction axis.
   *
   * @default undefined
   *
   * @example
   * // Single value
   * align="center"
   *
   * @example
   * // Responsive
   * align={{ sm: 'start', md:  'center', lg: 'end' }}
   */
  align?: ResponsiveValue<keyof typeof alignValues>;

  /**
   * Height of the stack container.
   * Currently only supports 'full' for 100% height.
   *
   * @default undefined
   *
   * @example
   * // Single value
   * height="full"
   *
   * @example
   * // Responsive
   * height={{ sm: 'full', lg: undefined }}
   */
  height?: ResponsiveValue<keyof typeof heightValues>;

  /**
   * Additional CSS classes to apply to the stack container.
   * Will be merged with the component's default classes.
   *
   * @example
   * className="my-custom-class bg-gray-100"
   */
  className?: string;

  /**
   * HTML tag to render for the stack container.
   * Allows semantic HTML while maintaining stack functionality.
   *
   * @default 'div'
   *
   * @example
   * tag="section"
   *
   * @example
   * tag="nav"
   *
   * @example
   * tag="article"
   */
  tag?: keyof JSX.IntrinsicElements;

  /**
   * Any additional DOM props (`data-*`, `aria-*`, `id`, `role`, `onClick`, …)
   * are forwarded to the underlying element. `aria-*` / `id` / `role` come
   * from `HTMLAttributes`; this index signature adds `data-*` support.
   */
  [dataAttr: `data-${string}`]: unknown;
}

interface StackItemProps
  extends PropsWithChildren,
    HTMLAttributes<HTMLElement> {
  /**
   * Flex behavior of the stack item.
   * Controls how the item grows and shrinks within the stack.
   *
   * @default 'none'
   *
   * @example
   * // Single value
   * flex="1"
   *
   * @example
   * // Responsive
   * flex={{ sm:  'none', md: '1', lg: 'auto' }}
   */
  flex?: ResponsiveValue<Exclude<keyof typeof flexValues, 'custom'>>;

  /**
   * Flex basis of the stack item.
   * Sets the initial size of the item before space distribution.
   *
   * @default 'auto'
   *
   * @example
   * basis="200px"
   *
   * @example
   * // Responsive
   * basis={{ sm:  '100px', md: '200px', lg: '300px' }}
   */
  basis?: ResponsiveValue<string>;

  /**
   * Flex grow factor.
   * Defines how much the item will grow relative to other items.
   * Only used when flex="custom".
   *
   * @default 1
   *
   * @example
   * grow={2}
   *
   * @example
   * // Responsive
   * grow={{ sm:  1, md: 2, lg: 3 }}
   */
  grow?: ResponsiveValue<number>;

  /**
   * Flex shrink factor.
   * Defines how much the item will shrink relative to other items.
   * Only used when flex="custom".
   *
   * @default 1
   *
   * @example
   * shrink={0}
   *
   * @example
   * // Responsive
   * shrink={{ sm: 1, md: 0 }}
   */
  shrink?: ResponsiveValue<number>;

  /**
   * HTML tag to render for the stack item.
   *
   * @default 'div'
   *
   * @example
   * tag="li"
   *
   * @example
   * tag="span"
   */
  tag?: keyof JSX.IntrinsicElements;
}

interface StackItemCustomFlexProps
  extends PropsWithChildren,
    HTMLAttributes<HTMLElement> {
  /**
   * Custom flex behavior with full control over grow, shrink, and basis.
   * When set to 'custom', you must provide grow, shrink, and basis values.
   */
  flex: ResponsiveValue<'custom'>;

  /**
   * Flex basis for custom flex.
   * Required when flex="custom".
   *
   * @example
   * basis="200px"
   */
  basis: ResponsiveValue<string>;

  /**
   * Flex grow factor for custom flex.
   * Required when flex="custom".
   *
   * @example
   * grow={2}
   */
  grow: ResponsiveValue<number>;

  /**
   * Flex shrink factor for custom flex.
   * Required when flex="custom".
   *
   * @example
   * shrink={1}
   */
  shrink: ResponsiveValue<number>;

  /**
   * HTML tag to render for the stack item.
   *
   * @default 'div'
   */
  tag?: keyof JSX.IntrinsicElements;
}

const getBaseValue = <T,>(value: ResponsiveValue<T>): T | undefined => {
  if (isResponsive(value)) {
    return undefined;
  }
  return value;
};

const StackItem = ({
  children,
  flex = 'none',
  basis = 'auto',
  grow = 1,
  shrink = 1,
  tag = 'div',
  className,
  ...rest
}: StackItemProps | StackItemCustomFlexProps) => {
  const getFlexClasses = useMemo(() => {
    const classes: string[] = [];

    if (isResponsive(flex)) {
      const entries = Object.entries(flex) as [
        Breakpoint,
        keyof typeof flexValues,
      ][];

      // Sort by breakpoint
      const sorted = entries.sort(
        (a, b) => breakpointOrder.indexOf(a[0]) - breakpointOrder.indexOf(b[0]),
      );

      // First value is the base class (without prefix)
      if (sorted.length > 0 && sorted[0][1] !== 'custom') {
        classes.push(flexClasses[sorted[0][1] as keyof typeof flexClasses]);
      }

      // Add all responsive classes
      sorted.forEach(([bp, value]) => {
        if (value !== 'custom') {
          const key = `${bp}:${value}` as keyof typeof flexClasses;
          if (flexClasses[key]) {
            classes.push(flexClasses[key]);
          }
        }
      });
    } else if (flex !== 'custom') {
      classes.push(flexClasses[flex as keyof typeof flexClasses]);
    }

    return classes;
  }, [flex]);

  const style: React.CSSProperties = {};
  const baseFlexValue = getBaseValue(flex);

  // Handle custom flex and basis. Non-responsive custom values are applied as
  // real inline flex properties — CSS variables alone are consumed by nothing
  // and left the item at the browser default (flex: 0 1 auto). The basis
  // assignment is gated on 'custom' so a responsive preset flex (e.g.
  // { sm: 'none', md: '1' }) never gets an inline flex-basis that would beat
  // its flex-1 class (1 1 0%).
  if (baseFlexValue === 'custom' || isResponsive(flex)) {
    if (isResponsive(basis)) {
      Object.entries(basis).forEach(([bp, value]) => {
        style[`--stack-item-basis-${bp}` as any] = value;
      });
    } else if (baseFlexValue === 'custom') {
      style.flexBasis = basis as string;
    }

    if (isResponsive(grow)) {
      Object.entries(grow).forEach(([bp, value]) => {
        style[`--stack-item-grow-${bp}` as any] = value?.toString();
      });
    } else if (baseFlexValue === 'custom') {
      style.flexGrow = Number(grow);
    }

    if (isResponsive(shrink)) {
      Object.entries(shrink).forEach(([bp, value]) => {
        style[`--stack-item-shrink-${bp}` as any] = value?.toString();
      });
    } else if (baseFlexValue === 'custom') {
      style.flexShrink = Number(shrink);
    }
  }

  return createElement(
    tag,
    {
      ...rest,
      className: cn(getFlexClasses, className),
      style: Object.keys(style).length > 0 ? style : undefined,
    },
    children,
  );
};

export const Stack = ({
  children,
  gap = 0,
  direction = directionValues.column,
  justify = justifyValues.start,
  align = undefined,
  height = undefined,
  tag = 'div',
  className,
  ...rest
}: Props) => {
  const getDirectionClasses = useMemo(() => {
    const classes: string[] = [];

    if (isResponsive(direction)) {
      const entries = Object.entries(direction) as [
        Breakpoint,
        keyof typeof directionValues,
      ][];

      // Sort by breakpoint
      const sorted = entries.sort(
        (a, b) => breakpointOrder.indexOf(a[0]) - breakpointOrder.indexOf(b[0]),
      );

      // First value is the base class (without prefix)
      if (sorted.length > 0) {
        classes.push(directionClasses[sorted[0][1]]);
      }

      // Add all responsive classes
      sorted.forEach(([bp, value]) => {
        const key = `${bp}:${value}` as keyof typeof directionClasses;
        if (directionClasses[key]) {
          classes.push(directionClasses[key]);
        }
      });
    } else {
      classes.push(directionClasses[direction]);
    }

    return classes;
  }, [direction]);

  const getJustifyClasses = useMemo(() => {
    const classes: string[] = [];

    if (isResponsive(justify)) {
      const entries = Object.entries(justify) as [
        Breakpoint,
        keyof typeof justifyValues,
      ][];

      const sorted = entries.sort(
        (a, b) => breakpointOrder.indexOf(a[0]) - breakpointOrder.indexOf(b[0]),
      );

      if (sorted.length > 0) {
        classes.push(justifyClasses[sorted[0][1]]);
      }

      // Add all responsive classes
      sorted.forEach(([bp, value]) => {
        if (value) {
          const key = `${bp}:${value}` as keyof typeof justifyClasses;
          if (justifyClasses[key]) {
            classes.push(justifyClasses[key]);
          }
        }
      });
    } else {
      classes.push(justifyClasses[justify]);
    }

    return classes;
  }, [justify]);

  const getAlignClasses = useMemo(() => {
    if (!align) return [];

    const classes: string[] = [];

    if (isResponsive(align)) {
      const entries = Object.entries(align) as [
        Breakpoint,
        keyof typeof alignValues,
      ][];

      const sorted = entries.sort(
        (a, b) => breakpointOrder.indexOf(a[0]) - breakpointOrder.indexOf(b[0]),
      );

      if (sorted.length > 0) {
        classes.push(alignClasses[sorted[0][1]]);
      }

      sorted.forEach(([bp, value]) => {
        if (value) {
          const key = `${bp}:${value}` as keyof typeof alignClasses;
          if (alignClasses[key]) {
            classes.push(alignClasses[key]);
          }
        }
      });
    } else {
      classes.push(alignClasses[align]);
    }

    return classes;
  }, [align]);

  const getHeightClasses = useMemo(() => {
    if (!height) return [];

    const classes: string[] = [];

    if (isResponsive(height)) {
      const entries = Object.entries(height) as [
        Breakpoint,
        keyof typeof heightValues,
      ][];

      const sorted = entries.sort(
        (a, b) => breakpointOrder.indexOf(a[0]) - breakpointOrder.indexOf(b[0]),
      );

      if (sorted.length > 0 && sorted[0][1] === 'full') {
        classes.push(heightClasses[sorted[0][1]]);
      }

      sorted.forEach(([bp, value]) => {
        if (value === 'full') {
          const key = `${bp}:${value}` as keyof typeof heightClasses;
          if (heightClasses[key]) {
            classes.push(heightClasses[key]);
          }
        }
      });
    } else if (height === 'full') {
      classes.push(heightClasses[height]);
    }

    return classes;
  }, [height]);

  // Handle responsive gap
  const gapClassesList = useMemo(() => {
    const classes: string[] = [];

    if (isResponsive(gap)) {
      const entries = Object.entries(gap) as [Breakpoint, SizeScales][];

      const sorted = entries.sort(
        (a, b) => breakpointOrder.indexOf(a[0]) - breakpointOrder.indexOf(b[0]),
      );

      // Process each breakpoint
      sorted.forEach(([bp, value], index) => {
        if (index === 0) {
          const baseKey = value as keyof typeof gapClasses;
          if (gapClasses[baseKey]) {
            classes.push(gapClasses[baseKey]);
          }
        }

        const key = `${bp}:${value}` as keyof typeof gapClasses;

        if (gapClasses[key]) {
          classes.push(gapClasses[key]);
        }
      });
    } else {
      const key = gap as keyof typeof gapClasses;
      if (gapClasses[key]) {
        classes.push(gapClasses[key]);
      }
    }

    return classes;
  }, [gap]);

  const classNames = cn(
    'flex',
    ...getDirectionClasses,
    ...getJustifyClasses,
    ...getAlignClasses,
    ...getHeightClasses,
    ...gapClassesList,
    className,
  );

  return createElement(
    tag,
    {
      ...rest,
      className: classNames,
    },
    children,
  );
};

Stack.Item = StackItem;
